import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, Volume2, VolumeX, Activity, MessageCircle, Lock } from 'lucide-react';
import audioService from '@/services/audioService';
import { useAuth } from '@/contexts/AuthContext';
import { getRandomSosRespirationSound, getSosRespirationSounds } from '@/services/sosAudioService';

const SOS = ({ t }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState('selection'); // selection, breathing, calm
  const [phase, setPhase] = useState('breathing'); // breathing, complete
  const [breathPhase, setBreathPhase] = useState('inhale'); // inhale, hold, exhale
  const [cycleCount, setCycleCount] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const [darkMode] = useState(true); // Mode nuit par défaut
  const [breathingMessages, setBreathingMessages] = useState({});
  const [voiceVolume, setVoiceVolume] = useState(0.30); // Voice volume control - 30% by default
  const [musicVolume, setMusicVolume] = useState(0.50); // Music volume control - 50% by default
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);
  const gainNodeRef = useRef(null);
  const voiceAudioRef = useRef(null);
  const backgroundMusicRef = useRef(null); // Background ambient music
  const timersRef = useRef([]); // Store all setTimeout IDs
  const voiceVolumeRef = useRef(0.30); // Ref to always get latest voice volume
  const musicVolumeRef = useRef(0.50); // Ref to always get latest music volume
  const respirationSoundsRef = useRef(null); // Store loaded R2 sounds
  
  // Web Audio API nodes for music boost (iPhone volume fix)
  const musicSourceNodeRef = useRef(null);
  const musicBoostGainNodeRef = useRef(null);
  const musicLimiterNodeRef = useRef(null);
  const musicMasterGainNodeRef = useRef(null);

  const TOTAL_CYCLES = 10; // ~2 minutes (12 seconds per cycle)
  const INHALE_DURATION = 4000; // 4 seconds
  const HOLD_DURATION = 2000; // 2 seconds
  const EXHALE_DURATION = 6000; // 6 seconds
  const CYCLE_DURATION = INHALE_DURATION + HOLD_DURATION + EXHALE_DURATION;

  // Update voice volume in real-time when slider changes
  useEffect(() => {
    voiceVolumeRef.current = voiceVolume; // Update ref
    if (voiceAudioRef.current) {
      voiceAudioRef.current.volume = voiceVolume;
    }
  }, [voiceVolume]);

  // Update music volume in real-time when slider changes
  useEffect(() => {
    musicVolumeRef.current = musicVolume; // Update ref
    
    // Web Audio API version (new implementation)
    if (musicMasterGainNodeRef.current) {
      musicMasterGainNodeRef.current.gain.value = musicVolume;
    }
    // Fallback for HTML Audio
    else if (backgroundMusicRef.current) {
      backgroundMusicRef.current.volume = musicVolume;
    }
  }, [musicVolume]);

  // Load background music sounds from R2 on mount
  useEffect(() => {
    const loadRespirationSounds = async () => {
      try {
        const sounds = await getSosRespirationSounds();
        
        if (sounds.length === 0) {
        }
        
        // Store sounds in a ref so they can be accessed in playBackgroundMusic
        if (!respirationSoundsRef.current) {
          respirationSoundsRef.current = sounds;
        }
      } catch (error) {
        console.error('❌ [SOS Respiration] Error loading sounds:', error);
        respirationSoundsRef.current = [];
      }
    };
    
    loadRespirationSounds();
  }, []);

  useEffect(() => {
    // Load breathing voice messages
    const loadBreathingMessages = async () => {
      if (!user) return;
      
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        // Use t?.locale to determine language (fr-FR or en-US)
        const lang = t?.locale?.startsWith('fr') ? 'fr' : 'en';
        const voice = 'hera'; // Same voice as Radio for UX consistency
        
        
        const response = await fetch(
          `${backendUrl}/api/radio/voice-messages/sos_breathing?user_id=${user.id}&lang=${lang}&voice=${voice}`
        );
        const data = await response.json();
        
        
        // Organize messages by phase - filter by language to avoid overlap
        const messagesByPhase = {};
        (data.messages || []).forEach(msg => {
          // Only use messages that match the current language
          if (msg.lang === lang) {
            messagesByPhase[msg.phase] = msg;
          } else {
          }
        });
        
        setBreathingMessages(messagesByPhase);
      } catch (error) {
        console.error('Error loading breathing messages:', error);
      }
    };
    
    loadBreathingMessages();
  }, [user, t?.locale]);

  // Initialize Web Audio API
  useEffect(() => {
    if (isSoundEnabled && !audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.connect(audioContextRef.current.destination);
      gainNodeRef.current.gain.value = 0.02; // Very low volume for ambient sound
    }
  }, [isSoundEnabled]);

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      stopAllAudio();
      
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  // Stop audio when changing modes
  useEffect(() => {
    if (mode === 'selection') {
      stopAllAudio();
    }
  }, [mode]);

  useEffect(() => {
    if (phase === 'breathing') {
      const interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 0) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [phase]);

  const playBreathSound = (frequency) => {
    if (!isSoundEnabled || !audioContextRef.current) return;

    // Stop previous oscillator
    if (oscillatorRef.current) {
      oscillatorRef.current.stop();
    }

    // Create new oscillator for subtle breathing sound
    oscillatorRef.current = audioContextRef.current.createOscillator();
    oscillatorRef.current.type = 'sine';
    oscillatorRef.current.frequency.value = frequency;
    oscillatorRef.current.connect(gainNodeRef.current);
    oscillatorRef.current.start();
  };

  const testDirectR2Audio = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const testUrl = `${backendUrl}/api/r2/proxy?key=SosRespiration/Respiration_003.mp3`;
    
    const audio = new Audio(testUrl);
    audio.volume = 0.5;
    
    audio.play()
      .then(() => {
      })
      .catch(err => {
        console.error('❌ [SOS Respiration DEBUG] Proxied audio.play() error:', err);
      });
      
    audio.onerror = (e) => {
      console.error('❌ [SOS Respiration DEBUG] Proxied audio element error:', e);
    };
  };

  const playVoiceMessage = (phase) => {
    if (!isSoundEnabled) return;
    
    const message = breathingMessages[phase];
    if (!message || !message.id) {
      return;
    }
    
    try {
      // Stop any currently playing voice message
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
        voiceAudioRef.current = null;
      }
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const audioUrl = `${backendUrl}/api/radio/voice-audio/${message.id}`;
      
      // Use ref to get the LATEST volume value (not captured in closure)
      const currentVolume = voiceVolumeRef.current;
      // Apply 4x amplification to voice volume (30% slider = 100% actual volume, capped)
      const amplifiedVolume = Math.min(currentVolume * 4, 1.0);
      
      const audio = new Audio(audioUrl);
      audio.volume = amplifiedVolume; // Use amplified volume
      
      // Store reference to control volume later
      voiceAudioRef.current = audio;
      
      audio.play().catch(err => {
      });

      // ── Duck background music while voice is playing ──
      if (musicMasterGainNodeRef.current && audioContextRef.current) {
        const ctx = audioContextRef.current;
        const gain = musicMasterGainNodeRef.current.gain;
        gain.setValueAtTime(gain.value, ctx.currentTime);
        gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.5);
      }

      audio.onended = () => {
        voiceAudioRef.current = null;
        // Restore music volume after voice ends
        if (musicMasterGainNodeRef.current && audioContextRef.current) {
          const ctx = audioContextRef.current;
          const gain = musicMasterGainNodeRef.current.gain;
          gain.setValueAtTime(gain.value, ctx.currentTime);
          gain.linearRampToValueAtTime(musicVolumeRef.current, ctx.currentTime + 1.5);
        }
      };
    } catch (error) {
      console.error('Error playing voice message:', error);
    }
  };

  const playBackgroundMusic = async () => {
    if (!isSoundEnabled) return;
    
    try {
      // Initialize Web Audio API context if not already done
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        gainNodeRef.current = audioContextRef.current.createGain();
        gainNodeRef.current.connect(audioContextRef.current.destination);
      }
      
      const audioContext = audioContextRef.current;
      
      // Create audio chain with boost (like Radio Hearen)
      // Music Master Gain (controlled by slider)
      musicMasterGainNodeRef.current = audioContext.createGain();
      musicMasterGainNodeRef.current.gain.value = 0; // Start at 0 for fade-in
      musicMasterGainNodeRef.current.connect(audioContext.destination);
      
      // Radio Boost: +8.6 dB (2.7x) for iPhone
      musicBoostGainNodeRef.current = audioContext.createGain();
      musicBoostGainNodeRef.current.gain.value = 2.7; // +8.6 dB boost
      
      // Limiter to prevent clipping
      musicLimiterNodeRef.current = audioContext.createDynamicsCompressor();
      musicLimiterNodeRef.current.threshold.value = -1.0;
      musicLimiterNodeRef.current.knee.value = 2.0;
      musicLimiterNodeRef.current.ratio.value = 20.0;
      musicLimiterNodeRef.current.attack.value = 0.003;
      musicLimiterNodeRef.current.release.value = 0.025;
      
      // Connect boost chain: boostGain -> limiter -> masterGain
      musicBoostGainNodeRef.current.connect(musicLimiterNodeRef.current);
      musicLimiterNodeRef.current.connect(musicMasterGainNodeRef.current);
      
      
      // Load audio file from Cloudflare R2 (SOS Respiration sounds)
      // ⚠️ These are NOT therapy sounds - no EQ, no notch, no tracking
      let musicPath = null;
      
      // Get sounds from ref (loaded on mount)
      const availableSounds = respirationSoundsRef.current;
      
      
      if (!availableSounds || availableSounds.length === 0) {
        
        try {
          const sounds = await getSosRespirationSounds();
          
          if (sounds.length > 0) {
            respirationSoundsRef.current = sounds;
            const randomSound = sounds[Math.floor(Math.random() * sounds.length)];
            musicPath = randomSound.url;
          }
        } catch (error) {
          console.error('❌ [SOS Respiration] Failed to load sounds:', error);
        }
      } else {
        // Pick a random sound from loaded sounds
        const randomIndex = Math.floor(Math.random() * availableSounds.length);
        const randomSound = availableSounds[randomIndex];
        musicPath = randomSound.url;
      }
      
      // If no R2 sound available, skip background music
      if (!musicPath) {
        return;
      }
      
      const response = await fetch(musicPath);
      
      const arrayBuffer = await response.arrayBuffer();
      
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Create source and connect to boost chain
      musicSourceNodeRef.current = audioContext.createBufferSource();
      musicSourceNodeRef.current.buffer = audioBuffer;
      musicSourceNodeRef.current.loop = true;
      musicSourceNodeRef.current.connect(musicBoostGainNodeRef.current);
      
      // Start playback
      musicSourceNodeRef.current.start(0);
      
      // Fade in over 4 seconds
      const targetVolume = musicVolumeRef.current;
      const currentTime = audioContext.currentTime;
      musicMasterGainNodeRef.current.gain.setValueAtTime(0, currentTime);
      musicMasterGainNodeRef.current.gain.linearRampToValueAtTime(targetVolume, currentTime + 4);
      
    } catch (error) {
      console.error('Error starting background music:', error);
    }
  };

  const stopBackgroundMusic = (immediate = false) => {
    if (!musicSourceNodeRef.current && !backgroundMusicRef.current) return;
    
    try {
      // Web Audio API version (new implementation)
      if (musicMasterGainNodeRef.current && audioContextRef.current) {
        const audioContext = audioContextRef.current;
        const currentTime = audioContext.currentTime;
        
        if (immediate) {
          // Immediate stop (for terminer button)
          musicMasterGainNodeRef.current.gain.setValueAtTime(0, currentTime);
          
          // Stop source immediately
          if (musicSourceNodeRef.current) {
            try {
              musicSourceNodeRef.current.stop();
            } catch (e) {
              // Already stopped
            }
            musicSourceNodeRef.current = null;
          }
          
          // Disconnect nodes immediately
          if (musicBoostGainNodeRef.current) {
            musicBoostGainNodeRef.current.disconnect();
            musicBoostGainNodeRef.current = null;
          }
          if (musicLimiterNodeRef.current) {
            musicLimiterNodeRef.current.disconnect();
            musicLimiterNodeRef.current = null;
          }
          if (musicMasterGainNodeRef.current) {
            musicMasterGainNodeRef.current.disconnect();
            musicMasterGainNodeRef.current = null;
          }
          
        } else {
          // Fade out over 2 seconds using Web Audio API
          musicMasterGainNodeRef.current.gain.setValueAtTime(
            musicMasterGainNodeRef.current.gain.value, 
            currentTime
          );
          musicMasterGainNodeRef.current.gain.linearRampToValueAtTime(0, currentTime + 2);
          
          // Stop source after fade out
          setTimeout(() => {
            if (musicSourceNodeRef.current) {
              try {
                musicSourceNodeRef.current.stop();
              } catch (e) {
                // Already stopped
              }
              musicSourceNodeRef.current = null;
            }
            
            // Disconnect nodes
            if (musicBoostGainNodeRef.current) {
              musicBoostGainNodeRef.current.disconnect();
              musicBoostGainNodeRef.current = null;
            }
            if (musicLimiterNodeRef.current) {
              musicLimiterNodeRef.current.disconnect();
              musicLimiterNodeRef.current = null;
            }
            if (musicMasterGainNodeRef.current) {
              musicMasterGainNodeRef.current.disconnect();
              musicMasterGainNodeRef.current = null;
            }
          }, 2000);
          
        }
      }
      // Fallback for old HTML Audio implementation
      else if (backgroundMusicRef.current) {
        if (immediate) {
          // Immediate stop
          backgroundMusicRef.current.pause();
          backgroundMusicRef.current.currentTime = 0;
          backgroundMusicRef.current = null;
        } else {
          // Fade out
          let volume = backgroundMusicRef.current.volume;
          const fadeOut = setInterval(() => {
            if (volume > 0.01) {
              volume -= 0.01;
              if (backgroundMusicRef.current) {
                backgroundMusicRef.current.volume = Math.max(volume, 0);
              }
            } else {
              clearInterval(fadeOut);
              if (backgroundMusicRef.current) {
                backgroundMusicRef.current.pause();
                backgroundMusicRef.current.currentTime = 0;
                backgroundMusicRef.current = null;
              }
            }
          }, 50);
          
        }
      }
    } catch (error) {
      console.error('Error stopping background music:', error);
    }
  };

  const startBreathing = () => {
    setMode('breathing');
    setPhase('breathing');
    setCycleCount(0);
    setBreathPhase('inhale');
    setTimeRemaining(Math.floor((TOTAL_CYCLES * CYCLE_DURATION) / 1000));
    playBackgroundMusic(); // Start ambient music
    runBreathingCycle();
  };

  const startCalm = () => {
    setMode('calm');
    navigate('/sos-calm');
  };

  const stopAllAudio = () => {
    
    // Cancel breathing cycle
    if (timersRef.current.cancel) {
      timersRef.current.cancel();
    }
    
    // Clear all timers
    timersRef.current.forEach(timer => {
      if (typeof timer === 'number') {
        clearTimeout(timer);
      }
    });
    timersRef.current = [];
    
    // Stop oscillator
    if (oscillatorRef.current) {
      try {
        oscillatorRef.current.stop();
      } catch (e) {
        // Already stopped
      }
      oscillatorRef.current = null;
    }
    
    // Stop voice audio COMPLETELY
    if (voiceAudioRef.current) {
      try {
        voiceAudioRef.current.pause();
        voiceAudioRef.current.currentTime = 0;
        voiceAudioRef.current.src = ''; // Remove source
        voiceAudioRef.current.load(); // Force reload
      } catch (e) {
      }
      voiceAudioRef.current = null;
    }
    
    // Stop background music IMMEDIATELY (no fade)
    if (backgroundMusicRef.current || musicSourceNodeRef.current) {
      stopBackgroundMusic(true); // immediate = true
    }
    
  };

  const runBreathingCycle = () => {
    let currentCycle = 0;
    let isCancelled = false;

    const runCycle = () => {
      if (isCancelled || currentCycle >= TOTAL_CYCLES) {
        if (currentCycle >= TOTAL_CYCLES) {
          setPhase('complete');
          // Music already faded out, just ensure it's stopped
          stopBackgroundMusic();
        }
        if (oscillatorRef.current) {
          try { oscillatorRef.current.stop(); } catch(e) {}
        }
        if (voiceAudioRef.current) {
          voiceAudioRef.current.pause();
          voiceAudioRef.current = null;
        }
        return;
      }

      // If this is the last cycle, schedule music fade out 2 seconds before end
      if (currentCycle === TOTAL_CYCLES - 1) {
        const fadeOutTiming = CYCLE_DURATION - 2000; // 2 seconds before end of last cycle
        const fadeTimer = setTimeout(() => {
          if (backgroundMusicRef.current) {
            // Gradual fade out over 2 seconds
            let volume = backgroundMusicRef.current.volume;
            const fadeOutInterval = setInterval(() => {
              if (volume > 0.001) {
                volume -= 0.001;
                if (backgroundMusicRef.current) {
                  backgroundMusicRef.current.volume = Math.max(volume, 0);
                }
              } else {
                clearInterval(fadeOutInterval);
              }
            }, 20); // Update every 20ms for smooth fade
          }
        }, fadeOutTiming);
        timersRef.current.push(fadeTimer);
      }

      // Inhale
      setBreathPhase('inhale');
      playVoiceMessage('inhale');
      
      const timer1 = setTimeout(() => {
        if (isCancelled) return;
        
        // Hold
        setBreathPhase('hold');
        if (oscillatorRef.current) {
          try { oscillatorRef.current.stop(); } catch(e) {}
        }
        playVoiceMessage('hold');
        
        const timer2 = setTimeout(() => {
          if (isCancelled) return;
          
          // Exhale
          setBreathPhase('exhale');
          playVoiceMessage('exhale');
          
          const timer3 = setTimeout(() => {
            if (isCancelled) return;
            
            currentCycle++;
            setCycleCount(currentCycle);
            runCycle();
          }, EXHALE_DURATION);
          timersRef.current.push(timer3);
        }, HOLD_DURATION);
        timersRef.current.push(timer2);
      }, INHALE_DURATION);
      timersRef.current.push(timer1);
    };

    // Store cancel function
    timersRef.current.cancel = () => {
      isCancelled = true;
    };

    runCycle();
  };

  const getBreathInstruction = () => {
    switch (breathPhase) {
      case 'inhale':
        return t?.locale === 'fr-FR' ? 'Inspirez doucement...' : 'Breathe in gently...';
      case 'hold':
        return t?.locale === 'fr-FR' ? 'Retenez...' : 'Hold...';
      case 'exhale':
        return t?.locale === 'fr-FR' ? 'Expirez lentement...' : 'Breathe out slowly...';
      default:
        return '';
    }
  };

  const getCircleSize = () => {
    switch (breathPhase) {
      case 'inhale':
        return 'scale-150';
      case 'hold':
        return 'scale-150';
      case 'exhale':
        return 'scale-75';
      default:
        return 'scale-100';
    }
  };

  const getCircleTransition = () => {
    switch (breathPhase) {
      case 'inhale':
        return `transform ${INHALE_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      case 'hold':
        return `transform ${HOLD_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      case 'exhale':
        return `transform ${EXHALE_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      default:
        return 'transform 1000ms ease-in-out';
    }
  };

  // Selection Screen - Accès rapide
  if (mode === 'selection') {
    return (
      <div className="min-h-screen bg-gray-900 pb-40">
        <div className="max-w-2xl mx-auto px-6 pt-20 pb-8">
          {/* Compact Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm mb-3">
              <Heart size={32} className="text-white" fill="white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">
              {t?.locale === 'fr-FR' ? 'SOS — Apaisement' : 'SOS — Relief'}
            </h1>
            <p className="text-white/60 text-sm">
              {t?.locale === 'fr-FR' 
                ? 'Suivez ces étapes dans l\'ordre'
                : 'Follow these steps in order'}
            </p>
            <p className="text-white/40 text-xs italic mt-2">
              {t?.locale === 'fr-FR' 
                ? 'Apaisement immédiat — sans réglage ni correction audio.'
                : 'Immediate relief — no audio adjustment or correction.'}
            </p>
          </div>

          {/* Ordered Steps */}
          <div className="space-y-4">
            {/* STEP 1 - Advanced Breathing for PREMIUM / Simple Breathing for FREE */}
            {(user?.isPremium || user?.subscription === 'premium' || user?.role === 'admin') ? (
              // PREMIUM: Advanced Breathing as STEP 1
              <button
                onClick={() => navigate('/breathing-premium')}
                className="w-full rounded-2xl p-6 text-left hover:shadow-xl transition-all hover:scale-[1.02] active:scale-95 relative overflow-hidden"
              >
                {/* Elegant gradient background */}
                <div 
                  className="absolute inset-0"
                  style={{
                    background: `
                      linear-gradient(135deg, rgba(111, 66, 234, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%),
                      repeating-linear-gradient(
                        135deg,
                        transparent,
                        transparent 10px,
                        rgba(255, 255, 255, 0.03) 10px,
                        rgba(255, 255, 255, 0.03) 20px
                      )
                    `,
                  }}
                />
                
                {/* Step Badge */}
                <div className="absolute -top-2 -left-2 w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-500 text-gray-900 rounded-full flex items-center justify-center font-bold text-lg shadow-lg border-2 border-white z-10">
                  1
                </div>
                
                <div className="flex items-center gap-4 relative z-10">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                    <span className="text-3xl">🫁</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-yellow-300 bg-yellow-500/20 px-2 py-1 rounded">
                        {t?.locale === 'fr-FR' ? 'PRIORITÉ' : 'PRIORITY'}
                      </span>
                    </div>
                    <h2 className="text-xl font-bold text-white mb-1">
                      {t?.locale === 'fr-FR' ? 'Respiration Avancée' : 'Advanced Breathing'}
                    </h2>
                    <p className="text-white/70 text-sm">
                      {t?.locale === 'fr-FR' ? '8 exercices • Musique R2 • Voix guidée Hera' : '8 exercises • R2 music • Hera guided voice'}
                    </p>
                  </div>
                  <div className="text-white text-2xl">→</div>
                </div>
              </button>
            ) : (
              // FREE: Simple Breathing as STEP 1
              <button
                onClick={() => navigate('/breathing-premium')}
                className="w-full rounded-2xl p-6 text-left hover:shadow-xl transition-all hover:scale-[1.02] active:scale-95 relative overflow-hidden"
              >
                {/* Elegant texture background - Green for FREE */}
                <div 
                  className="absolute inset-0"
                  style={{
                    background: `
                      linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%),
                      repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 10px,
                        rgba(255, 255, 255, 0.03) 10px,
                        rgba(255, 255, 255, 0.03) 20px
                      )
                    `,
                  }}
                />
                
                {/* Step Badge */}
                <div className="absolute -top-2 -left-2 w-10 h-10 bg-emerald-400 text-gray-900 rounded-full flex items-center justify-center font-bold text-lg shadow-lg border-2 border-white z-10">
                  1
                </div>
                
                <div className="flex items-center gap-4 relative z-10">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                    <Activity size={28} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-yellow-300 bg-yellow-500/20 px-2 py-1 rounded">
                        {t?.locale === 'fr-FR' ? 'PRIORITÉ' : 'PRIORITY'}
                      </span>
                    </div>
                    <h2 className="text-xl font-bold text-white mb-1">
                      {t?.locale === 'fr-FR' ? 'Contrôler la respiration' : 'Control Breathing'}
                    </h2>
                    <p className="text-white/70 text-sm">
                      {t?.locale === 'fr-FR' ? 'Exercice guidé de 1 minute (4-2-6)' : '1-minute guided exercise (4-2-6)'}
                    </p>
                  </div>
                  <div className="text-white/60 text-xs bg-white/10 px-3 py-1 rounded-full">
                    {t?.locale === 'fr-FR' ? '1 min' : '1 min'}
                  </div>
                </div>
              </button>
            )}

            {/* Divider with "Puis" */}
            <div className="flex items-center justify-center py-2">
              <div className="h-px bg-white/20 flex-1 max-w-[100px]"></div>
              <span className="px-4 text-white/40 text-sm font-medium">
                {t?.locale === 'fr-FR' ? 'Puis' : 'Then'}
              </span>
              <div className="h-px bg-white/20 flex-1 max-w-[100px]"></div>
            </div>

            {/* STEP 2 - Immediate Calm - SECONDARY */}
            <button
              onClick={startCalm}
              className="w-full rounded-2xl p-6 text-left hover:shadow-xl transition-all hover:scale-[1.02] active:scale-95 relative overflow-hidden"
            >
              {/* Elegant texture background */}
              <div 
                className="absolute inset-0"
                style={{
                  background: `
                    linear-gradient(135deg, rgba(22, 163, 74, 0.95) 0%, rgba(20, 184, 166, 0.95) 100%),
                    repeating-linear-gradient(
                      -45deg,
                      transparent,
                      transparent 10px,
                      rgba(255, 255, 255, 0.03) 10px,
                      rgba(255, 255, 255, 0.03) 20px
                    )
                  `,
                }}
              />
              
              {/* Step Badge */}
              <div className="absolute -top-2 -left-2 w-10 h-10 bg-teal-400 text-gray-900 rounded-full flex items-center justify-center font-bold text-lg shadow-lg border-2 border-white z-10">
                2
              </div>
              
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                  <Volume2 size={28} className="text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-white mb-1">
                    {t?.locale === 'fr-FR' ? 'Musique & voix apaisantes' : 'Calming Music & Voice'}
                  </h2>
                  <p className="text-white/70 text-sm">
                    {t?.locale === 'fr-FR' ? 'Après avoir régulé votre respiration' : 'After regulating your breath'}
                  </p>
                </div>
                <div className="text-white/60 text-xs bg-white/10 px-3 py-1 rounded-full">
                  {t?.locale === 'fr-FR' ? 'Illimité' : 'Unlimited'}
                </div>
              </div>
            </button>

            {/* Divider with "Puis (optionnel)" */}
            <div className="flex items-center justify-center py-2">
              <div className="h-px bg-white/20 flex-1 max-w-[100px]"></div>
              <span className="px-4 text-white/40 text-sm font-medium">
                {t?.locale === 'fr-FR' ? 'Puis (optionnel)' : 'Then (optional)'}
              </span>
              <div className="h-px bg-white/20 flex-1 max-w-[100px]"></div>
            </div>

            {/*
  STEP 3 removed for V1 stability
  {(() => {
    const hasPremium = user?.isPremium || user?.subscription === 'premium' || user?.role === 'admin';
    return (
      <button
        onClick={() => hasPremium ? navigate('/assistant') : navigate('/premium')}
        className="w-full rounded-2xl p-6 text-left hover:shadow-xl transition-all hover:scale-[1.02] active:scale-95 relative overflow-hidden"
        data-testid="sos-assistant-card"
      >
        <div 
          className="absolute inset-0"
          style={{
            background: hasPremium
              ? `linear-gradient(135deg, rgba(168, 85, 247, 0.95) 0%, rgba(236, 72, 153, 0.95) 100%),
                 repeating-linear-gradient(90deg, transparent, transparent 10px, rgba(255, 255, 255, 0.03) 10px, rgba(255, 255, 255, 0.03) 20px)`
              : `linear-gradient(135deg, rgba(100, 80, 140, 0.7) 0%, rgba(140, 70, 110, 0.7) 100%)`,
          }}
        />
        
        <div className="absolute -top-2 -left-2 w-10 h-10 bg-pink-400 text-gray-900 rounded-full flex items-center justify-center font-bold text-lg shadow-lg border-2 border-white z-10">
          3
        </div>
        
        <div className="flex items-center gap-4 relative z-10">
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm flex-shrink-0">
            {hasPremium
              ? <MessageCircle size={28} className="text-white" />
              : <Lock size={24} className="text-white/60" />}
          </div>
          <div className="flex-1">Talk with Hearen
            <h2 className="text-xl font-bold text-white mb-1">
              {t?.locale === 'fr-FR' ? 'Parler avec l\'assistant IA' : 'Talk with Hearen'}
            </h2>
            <p className="text-white/70 text-sm">
              {hasPremium
                ? (t?.locale === 'fr-FR' ? 'Soutien conversationnel personnalisé' : 'Personalized conversational support')
                : (t?.locale === 'fr-FR' ? 'Réservé aux membres Premium' : 'Premium members only')}
            </p>
          </div>
          {hasPremium ? (
            <div className="text-white/60 text-xs bg-white/10 px-3 py-1 rounded-full">
              {t?.locale === 'fr-FR' ? 'À votre rythme' : 'At your pace'}
            </div>
          ) : (
            <div className="text-yellow-300/90 text-xs font-semibold bg-yellow-400/15 px-3 py-1 rounded-full border border-yellow-400/20">
              Premium
            </div>
          )}
        </div>
      </button>
    );
  })()}
*/}
          </div>

          {/* Premium Upsell Banner - FREE Users Only */}
          {!(user?.isPremium || user?.subscription === 'premium' || user?.role === 'admin') && (
            <div className="mt-8">
              <div 
                className="relative overflow-hidden rounded-3xl p-8 backdrop-blur-md border border-white/20 shadow-[0_8px_32px_rgba(91,60,245,0.3)] cursor-pointer hover:shadow-[0_12px_40px_rgba(91,60,245,0.4)] transition-all hover:scale-[1.01]"
                onClick={() => navigate('/premium')}
                style={{
                  background: 'linear-gradient(135deg, rgba(91, 60, 245, 0.2) 0%, rgba(160, 76, 245, 0.2) 100%)',
                }}
              >
                {/* Decorative background pattern */}
                <div 
                  className="absolute inset-0 opacity-5"
                  style={{
                    backgroundImage: `repeating-linear-gradient(
                      45deg,
                      transparent,
                      transparent 20px,
                      rgba(255, 255, 255, 0.1) 20px,
                      rgba(255, 255, 255, 0.1) 40px
                    )`,
                  }}
                />

                {/* Lock icon */}
                <div className="absolute top-4 right-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <span className="text-2xl">🔒</span>
                </div>

                <div className="relative z-10">
                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-3">
                    {t?.locale === 'fr-FR' ? 'Débloquez les outils avancés SOS' : 'Unlock Advanced SOS Tools'}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-white/80 text-base mb-6 leading-relaxed">
                    {t?.locale === 'fr-FR' 
                      ? 'Respiration approfondie, suivi complet de vos progrès et bien plus encore.'
                      : 'Advanced breathing, complete progress tracking and much more.'}
                  </p>

                  {/* Features list */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              
                    <div className="flex items-center gap-2 text-white/90 text-sm">
                      <span className="text-lg">🤖</span>
                      <span>{t?.locale === 'fr-FR' ? 'Assistant IA illimité' : 'More Conversations with Hearen'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/90 text-sm">
                      <span className="text-lg">📊</span>
                      <span>{t?.locale === 'fr-FR' ? 'Suivi des progrès' : 'Progress tracking'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/90 text-sm">
                      <span className="text-lg">🎯</span>
                      <span>{t?.locale === 'fr-FR' ? 'Coach personnalisé' : 'Personalized coach'}</span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/premium');
                    }}
                    className="w-full bg-gradient-to-r from-[#5B3CF5] to-[#A04CF5] hover:from-[#4B2CE5] hover:to-[#9040E5] text-white font-semibold py-4 rounded-xl transition-all shadow-[0_4px_20px_rgba(91,60,245,0.4)] hover:shadow-[0_6px_30px_rgba(91,60,245,0.6)] text-base flex items-center justify-center gap-2"
                  >
                    <span className="text-xl">✨</span>
                    {t?.locale === 'fr-FR' ? 'Passer à Hearen Premium' : 'Upgrade to Hearen Premium'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Protocol Explanation */}
          <div className="mt-8 bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
            <p className="text-white/60 text-xs leading-relaxed text-center">
              {t?.locale === 'fr-FR' 
                ? '💡 Principe SOS : Commencez par ralentir votre respiration, puis laissez progressivement les sons installer un environnement plus calme.'
                : '💡 SOS Principle: Begin by slowing your breathing, then gradually let the sounds create a calmer environment.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Breathing Exercise Screen
  if (mode === 'breathing' && phase === 'breathing') {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-between py-8 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900'}`}>
        <div className="text-center w-full px-6">
          {/* Progress - Top */}
          <div className="text-white/60 text-sm mb-4">
            {t?.locale === 'fr-FR' ? 'Cycle' : 'Cycle'} {cycleCount + 1} / {TOTAL_CYCLES} • {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
          </div>

          {/* Breathing Circle - Center with more space */}
          <div className="relative w-64 h-64 mx-auto my-16">
            <div
              className={`absolute inset-0 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 shadow-2xl ${getCircleSize()}`}
              style={{
                transition: getCircleTransition(),
                filter: darkMode ? 'brightness(0.6)' : 'brightness(1)',
              }}
            />
            {/* Inner glow effect */}
            <div className="absolute inset-0 rounded-full bg-white/20 animate-pulse" />
          </div>

          {/* Instruction */}
          <h2 className="text-3xl font-light text-white mb-8">
            {getBreathInstruction()}
          </h2>

          {/* Volume Controls - During breathing - Horizontal layout */}
          <div className="max-w-3xl mx-auto bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Voice Volume */}
              <div className="space-y-2">
                <label className="text-white text-sm flex items-center justify-between">
                  <span>{t?.locale === 'fr-FR' ? '🗣️ Volume voix' : '🗣️ Voice volume'}</span>
                  <span className="text-white/70">{Math.round(voiceVolume * 100)}%</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={voiceVolume * 100}
                  onChange={(e) => {
                    const newVolume = e.target.value / 100;
                    setVoiceVolume(newVolume);
                    // Apply to currently playing voice if any
                    if (voiceAudioRef.current) {
                      voiceAudioRef.current.volume = newVolume;
                    }
                  }}
                  className="w-full h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer"
                  style={{
                    accentColor: '#3b82f6',
                    touchAction: 'none',
                    padding: '14px 0',
                    boxSizing: 'content-box'
                  }}
                />
              </div>

              {/* Music Volume */}
              <div className="space-y-2">
                <label className="text-white text-sm flex items-center justify-between">
                  <span>{t?.locale === 'fr-FR' ? '🎵 Volume musique' : '🎵 Music volume'}</span>
                  <span className="text-white/70">{Math.round(musicVolume * 100)}%</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={musicVolume * 100}
                  onChange={(e) => {
                    const newVolume = e.target.value / 100;
                    setMusicVolume(newVolume);
                    // Apply to currently playing music if any
                    if (backgroundMusicRef.current) {
                      backgroundMusicRef.current.volume = newVolume;
                    }
                  }}
                  className="w-full h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer"
                  style={{
                    accentColor: '#3b82f6',
                    touchAction: 'none',
                    padding: '14px 0',
                    boxSizing: 'content-box'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Skip button */}
          <button
            onClick={() => {
              setMode('selection');
              setPhase('breathing');
              if (oscillatorRef.current) {
                try {
                  oscillatorRef.current.stop();
                } catch (e) {}
              }
            }}
            className="text-white/40 hover:text-white/60 text-sm"
          >
            {t?.locale === 'fr-FR' ? 'Terminer l\'exercice' : 'End exercise'}
          </button>
        </div>
      </div>
    );
  }

  // Complete Screen
  return (
    <div className={`min-h-screen pb-40 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900'}`}>
      <div className="max-w-2xl mx-auto px-6 py-12 space-y-8 text-center">
        {/* Success Icon */}
        <div className="w-24 h-24 mx-auto bg-green-500/20 rounded-full flex items-center justify-center backdrop-blur-sm animate-pulse">
          <Heart size={48} className="text-green-400" fill="currentColor" />
        </div>

        {/* Congratulations Message */}
        <div className="space-y-4">
          <h1 className="text-3xl font-bold text-white">
            {t?.locale === 'fr-FR' ? 'Bravo !' : 'Well done!'}
          </h1>
          <p className="text-xl text-white/90 leading-relaxed">
            {t?.locale === 'fr-FR' 
              ? 'Vous venez de rétablir votre calme intérieur.'
              : 'You have just restored your inner calm.'}
          </p>
        </div>

        {/* Recommendations */}
        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20 text-left">
          <h3 className="text-lg font-semibold text-white mb-3">
            {t?.locale === 'fr-FR' ? 'Continuez votre bien-être' : 'Continue your wellness'}
          </h3>
          <p className="text-white/80 text-sm">
            {t?.locale === 'fr-FR' 
              ? 'Explorez nos écoutes personnalisées ou écoutez Radio Hearen pour prolonger cette sensation d\'apaisement.'
              : 'Explore our personalized sound therapies or listen to Radio Hearen to extend this feeling of calm.'}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            onClick={() => {
              stopAllAudio();
              setMode('selection');
              setPhase('breathing');
            }}
            className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30 py-6 rounded-2xl text-lg font-semibold"
          >
            {t?.locale === 'fr-FR' ? 'Retour aux options' : 'Back to options'}
          </Button>
          <Button
            onClick={() => navigate('/therapy')}
            className="w-full bg-white text-blue-900 hover:bg-white/90 py-6 rounded-2xl text-lg font-semibold shadow-xl"
          >
            {t?.locale === 'fr-FR' ? 'Accéder aux paysages sonores' : 'Go to Soundscapes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SOS;
